import { useState, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'src/components/ui/tabs';
import { TaskTypeViewRegionCard } from './view/TaskTypeView';
import type { TaskViewState } from './view/useTaskViewState';
import {
  regionTabValue,
  SIDE_PANEL_NOTES_TAB,
  SIDE_PANEL_TIMELINE_TAB,
  sidePanelTabs,
} from './sidePanelTabs';

/** Parenthetical count, matching the AP invoice Activity / Notes tabs. */
function TabCount({ count }: { count: number | null }) {
  if (count === null || count === 0) return null;
  return <span className="tabular-nums"> ({count})</span>;
}

/**
 * One tabbed panel for everything beside the main column. Side sections of
 * the task page render as tabs ahead of Task Activity and Task Notes, using
 * the same working copy and commit as the main column. Task Activity opens
 * with the task. Embedded records (an AP invoice, for example) keep their own
 * Activity and Notes labels.
 */
export function TaskReviewSidePanel({
  view,
  notes,
  timeline,
  notesCount = null,
  activityCount = null,
}: {
  view: TaskViewState;
  notes: ReactNode;
  timeline: ReactNode;
  /** Null while the thread is still loading; zero hides the badge. */
  notesCount?: number | null;
  /** Null while the audit trail is still loading; zero hides the badge. */
  activityCount?: number | null;
}) {
  const gt = useGT();
  const tabs = sidePanelTabs(view.asideRegions, {
    notes: gt('Task Notes', {
      context:
        'task review side panel tab for notes on the task, distinct from notes on an embedded record',
    }),
    timeline: gt('Task Activity', {
      context:
        'task review side panel tab for the task audit timeline, distinct from activity on an embedded record',
    }),
    untitled: (n) =>
      gt('Section {n}', {
        context: 'task review side panel tab for an untitled side section',
        n: String(n),
      }),
  });
  const [selected, setSelected] = useState(SIDE_PANEL_TIMELINE_TAB);
  // A tab removed by a type edit falls back to Task Activity.
  const active = tabs.some((tab) => tab.value === selected)
    ? selected
    : SIDE_PANEL_TIMELINE_TAB;

  return (
    <Tabs
      value={active}
      onValueChange={setSelected}
      className="min-h-0 flex-1"
      data-testid="task-review-side-panel"
    >
      <TabsList className="w-full justify-start overflow-x-auto">
        {tabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
            {tab.value === SIDE_PANEL_NOTES_TAB && (
              <TabCount count={notesCount} />
            )}
            {tab.value === SIDE_PANEL_TIMELINE_TAB && (
              <TabCount count={activityCount} />
            )}
          </TabsTrigger>
        ))}
      </TabsList>
      {view.asideRegions.map((region) => (
        <TabsContent
          key={region.id}
          value={regionTabValue(region.id)}
          className="min-h-0 flex-1 overflow-y-auto"
          inert={view.rematching}
          aria-busy={view.rematching}
        >
          <TaskTypeViewRegionCard
            region={region}
            hideTitle
            schemaFields={view.schemaFields}
            input={view.input}
            workingCopy={view.workingCopy}
            anchorType={view.anchorType}
            anchorId={view.anchorId}
            onCommit={view.commit}
          />
        </TabsContent>
      ))}
      <TabsContent
        value={SIDE_PANEL_NOTES_TAB}
        className="flex min-h-0 flex-1 flex-col"
      >
        {notes}
      </TabsContent>
      <TabsContent
        value={SIDE_PANEL_TIMELINE_TAB}
        className="flex min-h-0 flex-1 flex-col"
      >
        {timeline}
      </TabsContent>
    </Tabs>
  );
}
